import React, { useCallback, useMemo } from 'react'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { ProductCell } from './components/ProductCell'
import { Table } from './components/Table'
import { SquareAmountCell } from './components/SquareAmountCell'
import { EditOrderItemModal } from './components/EditOrderItemModal'
import { PackingCheckboxCell } from './components/PackingCheckboxCell'
import { useOrderDetailContext } from './OrderDetailContext'
import { FragmentType, graphql, useFragment } from '../../gql'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowDown } from '@fortawesome/pro-regular-svg-icons'
import { FragmentValue } from '../../lib/RequestUtils'
import { useSettings } from '../../contexts/SettingsContext'

type Props = {
  items: FragmentType<typeof OrderItemsTable_OrderItemFragment>[]
  // Packaging flow
  packaging?: boolean
  renderActions?: (item: FragmentValue<typeof OrderItemsTable_OrderItemFragment>) => React.ReactNode
  actionsWidth?: number
  movingOrderItemIds?: number[]
}

export const OrderItemsTable_OrderItemFragment = graphql(`
  fragment OrderItemsTable_OrderItemFragment on OrderItem {
    id
    name
    ean
    code
    note
    quantity
    quantityUnavailable
    product {
      id
      name
      ean
      ean2
      ean3
      ean4
      ean5
      code
      isBundle
      note
      bundleChildren {
        child {
          id
          name
          code
          note
          ean
          ean2
          ean3
          ean4
          ean5
          ...ProductCell_ProductFragment
        }
        quantity
      }
      ...ProductCell_ProductFragment
    }
    clearanceItems {
      id
      position
      amount
      expirationDate
      picked
      packed
      product {
        id
      }
    }
    ...EditOrderItemModal_OrderItemFragment
  }
`)

export const OrderItemsTable_OrderFragment = graphql(`
  fragment OrderItemsTable_OrderFragment on Order {
    id
    processStatus {
      id
    }
  }
`)

const columnHelper = createColumnHelper<
  FragmentValue<typeof OrderItemsTable_OrderItemFragment> & {
    product: { isBundleChild: boolean }
    parent: FragmentValue<typeof OrderItemsTable_OrderItemFragment>
  }
>()

export const OrderItemsTable: React.FC<Props> = ({
  items,
  renderActions,
  actionsWidth,
  movingOrderItemIds,
  packaging,
}) => {
  'use no memo'
  const orderItems = useFragment(OrderItemsTable_OrderItemFragment, items)
  const context = useOrderDetailContext()
  const order = useFragment(OrderItemsTable_OrderFragment, context.order)
  const settings = useSettings()

  type RowItem = FragmentValue<typeof OrderItemsTable_OrderItemFragment> & {
    product:
      | (NonNullable<FragmentValue<typeof OrderItemsTable_OrderItemFragment>['product']> & {
          isBundleChild?: boolean
        })
      | null
    parent?: FragmentValue<typeof OrderItemsTable_OrderItemFragment>
  }

  const tableData = useMemo<RowItem[]>(
    () =>
      orderItems.reduce<RowItem[]>((acc, item) => {
        acc.push(item as RowItem)
        if (item.product?.bundleChildren) {
          item.product.bundleChildren.forEach(child => {
            const childProduct = { ...child.child, isBundleChild: true } as RowItem['product']
            acc.push({
              ...(item as RowItem),
              product: childProduct,
              quantity: child.quantity * item.quantity,
              parent: item,
            })
          })
        }
        return acc
      }, []),
    [orderItems],
  )

  const totalQuantity = useMemo(() => {
    return tableData.reduce((acc, item) => {
      const isBundleParentRow = Boolean(item.product?.isBundle) && !item.product?.isBundleChild
      return isBundleParentRow ? acc : acc + item.quantity
    }, 0)
  }, [tableData])

  const columnVisibility = useMemo(
    () => ({
      'product.ean': !orderItems.every(
        el => !el?.product?.ean && !el?.product?.ean2 && !el?.product?.ean3 && !el?.product?.ean4 && !el?.product?.ean5,
      ),
      'product.code': !orderItems.every(el => !el?.product?.code),
      quantityUnavailable: !orderItems.every(el => el?.quantityUnavailable == 0),
      note: !orderItems.every(el => !el?.note),
      packed: order?.processStatus?.id === 'packing' && !settings.store.pickingPieceByPiece,
      progress: settings.store.pickingPieceByPiece,
    }),
    [orderItems, order?.processStatus?.id, settings.store],
  )

  const tableState = useMemo(
    () => ({
      columnVisibility,
    }),
    [columnVisibility],
  )

  const Columns = useMemo(
    () =>
      [
        columnHelper.accessor('product', {
          header: 'Zboží',
          cell: ({ getValue, row }) => {
            const product = getValue()
            const isBundleChild = row.original.product?.isBundleChild
            const effectiveName = isBundleChild ? product?.name : row.original.name
            return <ProductCell product={{ ...product, name: effectiveName }} isBundleChild={isBundleChild} />
          },
        }),
        columnHelper.display({
          id: 'product.ean',
          header: 'EAN',
          cell: ({ row }) => {
            const product = row.original.product
            const eans = [
              product?.ean && { label: 'EAN', value: product.ean },
              product?.ean2 && { label: 'EAN2', value: product.ean2 },
              product?.ean3 && { label: 'EAN3', value: product.ean3 },
              product?.ean4 && { label: 'EAN4', value: product.ean4 },
              product?.ean5 && { label: 'EAN5', value: product.ean5 },
            ].filter(Boolean) as Array<{ label: string; value: string }>

            if (eans.length === 0) return null

            // If only one EAN, show just the value
            if (eans.length === 1) {
              return <div className="text-sm">{eans[0].value}</div>
            }

            // If multiple EANs, show with labels
            return (
              <div className="flex flex-col gap-1">
                {eans.map((ean, index) => (
                  <div key={index} className="text-sm">
                    {ean.label}: {ean.value}
                  </div>
                ))}
              </div>
            )
          },
          size: 160,
        }),
        columnHelper.accessor('product.code', { header: 'Kód', size: 250 }),
        columnHelper.accessor('note', {
          header: 'Poznámka',
          cell: ({ getValue, row }) => (
            <span className="font-bold text-ellipsis overflow-hidden line-clamp-3">
              {!row.original.product?.isBundleChild ? getValue() : row.original.product.note}
            </span>
          ),
          size: 150,
        }),
        columnHelper.accessor('quantity', {
          header: () => (
            <div>
              <div className="absolute bottom-[-31px] w-[74px]">
                <SquareAmountCell className="rounded-t-none shadow-lg">{totalQuantity}</SquareAmountCell>
              </div>
              <div className={'text-center'}>Počet ks</div>
            </div>
          ),
          cell: ({ getValue, row }) => {
            const isBundleParentRow = Boolean(row.original.product?.isBundle) && !row.original.product?.isBundleChild
            if (isBundleParentRow) {
              return <SquareAmountCell className="opacity-20 grayscale">{getValue()}</SquareAmountCell>
            }
            return <SquareAmountCell>{getValue()}</SquareAmountCell>
          },
          size: 80,
        }),
        columnHelper.accessor('quantityUnavailable', {
          header: () => <div className={'text-center'}>Chybí ks</div>,
          cell: ({ getValue }) =>
            getValue() > 0 ? <SquareAmountCell className="square-red">{getValue()}</SquareAmountCell> : null,
          size: 80,
        }),
        columnHelper.display({
          id: 'progress',
          header: () => (
            <div className={'text-center'}>
              {order?.processStatus?.id === 'picking' ? 'Vyskladněno ks' : 'Zabaleno ks'}
            </div>
          ),
          cell: ({ row }) => {
            if (row.original.product?.isBundle) return null
            const cis = row.original.clearanceItems.filter(ci => ci.product?.id === row.original.product?.id) || []
            const value = (
              order?.processStatus?.id === 'picking'
                ? cis.reduce((sum, ci) => sum + (ci?.picked ?? 0), 0)
                : cis.reduce((sum, ci) => sum + (ci?.packed ?? 0), 0)
            ) as number
            return <SquareAmountCell>{value}</SquareAmountCell>
          },
          size: 100,
        }),
        columnHelper.display({
          id: 'packed',
          header: () => <div className={'text-center'}>Zabaleno</div>,
          cell: ({ row }) =>
            order?.processStatus?.id === 'packing' && !row.original.product?.isBundleChild ? (
              <PackingCheckboxCell orderItemId={row.original.id} orderId={order.id} />
            ) : null,
          size: 100,
        }),
        columnHelper.display({
          id: 'actions',
          cell: props =>
            !props.row.original.product?.isBundleChild &&
            (renderActions ? (
              <span className="flex flex-1 justify-end gap-2">
                {renderActions(props.row.original)}
                {packaging ? <EditOrderItemModal item={props.row.original} /> : null}
              </span>
            ) : (
              <span className="flex flex-1 justify-end gap-2">
                {packaging ? <EditOrderItemModal item={props.row.original} /> : null}
              </span>
            )),
          size: actionsWidth || 100,
        }),
      ].filter(Boolean),
    [order, renderActions, packaging, actionsWidth],
  )

  const table = useReactTable({
    columns: Columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    state: tableState,
    defaultColumn: {
      // @ts-ignore
      size: '1fr',
    },
  })

  const getClassnameForRow = useCallback(
    row => (movingOrderItemIds?.includes(row.original.id) ? 'bg-amber-200' : ''),
    [movingOrderItemIds],
  )

  return (
    <div className="relative">
      <Table table={table} getClassnameForRow={getClassnameForRow} />
    </div>
  )
}
